<template>
  <model-table
    ref="showingsTable"
    :collection="showings"
    :columns="columns"
    sort-by="time"
    :descending="false"
    :show-add="false"
    :show-edit="false"
    :actions="actions"
  >
    <template #header-controls>
      <div class="q-mt-xs">
        <q-btn-toggle
          v-model="period"
          class="q-my-xs q-mr-md"
          size="sm"
          outline
          toggle-color="primary"
          :options="periodOptions"
        />
        <q-btn-toggle
          v-model="published"
          class="q-my-xs"
          size="sm"
          outline
          toggle-color="primary"
          :options="publishedOptions"
        />
      </div>
    </template>
    <showing-form />
    <showing-change-dialog
      ref="showingChangeDialog"
      @change="$refs.showingsTable.handleChange()"
    />
  </model-table>
</template>

<script>
import { Notify } from 'quasar'
import { appEnv, serverUri } from 'src/config'
import ModelTable from 'src/modules/admin/components/ModelTable'
import ShowingChangeDialog from 'src/modules/admin/components/ShowingChangeDialog'
import QueryEncodableOptionsMixin from 'src/modules/admin/mixins/QueryEncodableOptionsMixin'
import ShowingForm from 'src/modules/admin/showing/ShowingForm'
import ShowingBadgesDisplayCell from 'src/modules/admin/showing-badge/components/ShowingBadgesDisplayCell'

const periodOptions = [
  { label: 'All', value: 'all' },
  { label: 'Current', value: 'current' },
  { label: 'Future', value: 'future' },
  { label: 'Past', value: 'past' }
]

const publishedOptions = [
  { label: 'All', value: 'all' },
  { label: 'Published', value: true },
  { label: 'Unpublished', value: false }
]

export default {
  name: 'ShowingTable',
  components: {
    ModelTable,
    ShowingForm,
    ShowingChangeDialog
  },
  configuration: ['currentCircuit'],
  mixins: [QueryEncodableOptionsMixin],
  models: {
    showings: {
      movie: {
        fields: ['id', 'name']
      },
      movieTerm: {
        fields: ['id'],
        showingBadges: {
          fields: ['id', 'name']
        }
      },
      screen: {
        fields: ['id', 'name']
      },
      showingBadges: {
        fields: ['id', 'name']
      },
      variables () {
        return {
          movieTermId: this.movieTermId,
          boxOfficeReportId: this.boxOfficeReportId,
          period: this.period === 'all' ? null : this.period,
          published: this.published === 'all' ? null : this.published
        }
      },
      immediate: false
    },
    currentSite: {
      customerioShowingChangeTemplateId: true,
      immediate: true
    }
  },
  props: {
    initialPeriod: {
      type: String,
      required: false,
      default: 'future'
    },
    movieTermId: {
      type: Number,
      required: false,
      default: null
    },
    boxOfficeReportId: {
      type: Number,
      required: false,
      default: null
    },
    columnFn: {
      type: Function,
      required: false,
      default: (columns) => columns
    }
  },
  data () {
    return {
      ShowingBadgesDisplayCell,
      period: this.initialPeriod,
      published: true
    }
  },
  computed: {
    defaultQueryEncodableOptions () {
      return {
        // from initial data
        period: 'future',
        published: true
      }
    },
    columns () {
      return _.compact(this.columnFn([
        { field: 'id' },
        { field: 'movie', component: 'model' },
        { field: 'movieTerm', component: 'model', editDisabled: true },
        { field: 'time', component: 'date-time' },
        { field: 'screen', component: 'model' },
        { field: 'showingBadges', limitValues: 4, component: ShowingBadgesDisplayCell },
        { field: 'published', component: 'checkmark-or-negative' },
        { field: 'ticketsSold' },
        { name: 'actions', align: 'right' }
      ]))
    },
    actions () {
      const needsShowingChangeTemplateId = (
        appEnv !== 'development' &&
        this.currentCircuit.customerioWorkspaceIsConfigured &&
        !this.currentSite.customerioShowingChangeTemplateId
      )

      return _.compact([
        {
          label: 'Modify Showing (Beta)',
          icon: 'edit',
          disabled: function (showing) {
            return showing.published || needsShowingChangeTemplateId
          },
          disabledTooltip: (showing) => showing.published
            ? 'This showing must be unpublished before changes can be made.'
            : needsShowingChangeTemplateId ? 'Missing Customer.io template ID for showing change email. Contact support for assistance.' : '',
          handler: this.showShowingChangeDialog
        },
        {
          label: 'Recompute',
          icon: 'assignment',
          tooltip: 'Recompute associated data for this showing, including badges, price card, and movie term.',
          disabled: (showing) => showing.isLocal,
          disabledTooltip: (showing) => showing.isLocal ? 'This showing must be saved before it can be recomputed.' : '',
          handler: this.recomputeShowing
        },
        {
          label: 'Export',
          icon: 'launch',
          disabled: (showing) => showing.isLocal,
          disabledTooltip: (showing) => showing.isLocal ? 'This showing must be saved before it can be exported.' : '',
          handler: this.exportShowing
        }
      ])
    }
  },
  created () {
    _.extend(this, { periodOptions, publishedOptions })
  },
  methods: {
    decodeUrlParamOption (key, value) {
      switch (key) {
        case 'period':
          return _.includes(_.map(this.periodOptions, 'value'), value) ? value : undefined
        case 'published':
          return value === 'all' ? value : String(value) === 'true'
        default:
          return undefined
      }
    },
    exportShowing (showing) {
      window.location.href = `${serverUri}/export_showing?site_id=${this.currentSite.id}&showing_id=${showing.id}`

      Notify.create({
        message: 'Download Started',
        position: 'top',
        type: 'positive'
      })
    },
    async recomputeShowing (showing) {
      const result = await showing.recomputeData()

      if (!_.isEmpty(result.errors)) {
        Notify.create({
          message: 'Error recomputing showing',
          position: 'top',
          type: 'negative'
        })
      } else {
        Notify.create({
          message: 'Showing recomputed',
          position: 'top',
          type: 'positive'
        })
      }
    },
    showShowingChangeDialog (showing) {
      this.$refs.showingChangeDialog.show(showing)
    }
  }
}
</script>
